import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { Observable } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs';
import { AuthService } from '../services/auth.service';

/**
 * Refresh-aware route guard.
 *
 * 1. Waits for startup hydration (initializeSession) to settle before acting
 *    so that a browser-restart session restore does not race with navigation.
 * 2. Delegates token validity and refresh to AuthService.ensureValidToken(),
 *    which deduplicates concurrent refresh calls automatically.
 * 3. Redirects to /login only after a failed refresh, preventing loops caused
 *    by optimistic redirect before the refresh cookie has been evaluated.
 */
export const authGuard: CanActivateFn = (): Observable<boolean | UrlTree> => {
  const auth = inject(AuthService);
  const router = inject(Router);

  return auth.initialized$.pipe(
    filter(initialized => initialized),
    take(1),
    switchMap(() => auth.ensureValidToken()),
    map(valid => valid || router.createUrlTree(['/login']))
  );
};
